import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [sessionWarning, setSessionWarning] = useState(false);
  const sessionCheckInterval = useRef(null);
  const sessionWarningTimeout = useRef(null);
  const activityInterval = useRef(null);
  const lastActivityTime = useRef(Date.now());

  // Check if user has admin role
  const isAdmin = userRoles.some(role =>
    ['admin', 'super_admin'].includes(role)
  );

  // Session management
  const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
  const SESSION_WARNING_TIME = 10 * 60 * 1000; // Warn 10 minutes before expiry
  const TOKEN_EXPIRY_BUFFER = 60 * 1000; // 1 minute buffer
  const ACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity before showing warnings
  const ACTIVITY_CHECK_INTERVAL = 60 * 1000; // Check activity every minute

  // Validate token and check expiry
  const validateToken = async () => {
    if (!token || !user) return false;

    try {
      // Try to validate token by making a lightweight API call
      await axios.get('/auth/profile');
      return true;
    } catch (error) {
      console.log('Token validation failed:', error.response?.status);
      return false;
    }
  };

  // Check if token is about to expire
  const checkTokenExpiry = () => {
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;

      // If token expires within warning time, show warning
      if (timeUntilExpiry <= SESSION_WARNING_TIME && timeUntilExpiry > 0) {
        setSessionWarning(true);
        return true;
      }

      // If token has expired or will expire very soon, logout
      if (timeUntilExpiry <= TOKEN_EXPIRY_BUFFER) {
        console.log('Session expired, logging out');
        logout();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return false;
    }
  };

  // Start session monitoring
  const startSessionMonitoring = () => {
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
    }

    sessionCheckInterval.current = setInterval(async () => {
      if (token && user) {
        const isValid = await validateToken();
        if (!isValid) {
          console.log('Token invalid, logging out');
          logout();
          return;
        }

        checkTokenExpiry();
      }
    }, SESSION_CHECK_INTERVAL);

    // Initial check
    checkTokenExpiry();
  };

  // Stop session monitoring
  const stopSessionMonitoring = () => {
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
      sessionCheckInterval.current = null;
    }
    if (sessionWarningTimeout.current) {
      clearTimeout(sessionWarningTimeout.current);
      sessionWarningTimeout.current = null;
    }
    setSessionWarning(false);
  };

  // Update last activity time
  const updateActivity = () => {
    lastActivityTime.current = Date.now();
    // If there was a warning, clear it since user is active
    if (sessionWarning) {
      setSessionWarning(false);
    }
  };

  // Check for inactivity and token expiry
  const checkInactivity = () => {
    if (!token || !user) return;

    const currentTime = Date.now();
    const timeSinceLastActivity = currentTime - lastActivityTime.current;

    // Check token expiry first
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiryTime = payload.exp * 1000;
    const timeUntilExpiry = expiryTime - currentTime;

    // Logout if token has expired (regardless of activity)
    if (timeUntilExpiry <= TOKEN_EXPIRY_BUFFER) {
      console.log('Session expired due to token expiry, logging out');
      logout();
      return;
    }

    // Logout if user has been inactive for ACTIVITY_TIMEOUT
    if (timeSinceLastActivity >= ACTIVITY_TIMEOUT) {
      console.log('Session expired due to inactivity, logging out');
      logout();
    }
  };

  // Start activity monitoring
  const startActivityMonitoring = () => {
    // Reset activity time
    lastActivityTime.current = Date.now();

    // Start inactivity check interval
    if (activityInterval.current) {
      clearInterval(activityInterval.current);
    }

    activityInterval.current = setInterval(() => {
      checkInactivity();
    }, ACTIVITY_CHECK_INTERVAL);

    // Add event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });
  };

  // Stop activity monitoring
  const stopActivityMonitoring = () => {
    if (activityInterval.current) {
      clearInterval(activityInterval.current);
      activityInterval.current = null;
    }

    // Remove event listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, updateActivity, true);
    });
  };

  // Extend session (could be called when user interacts)
  const extendSession = () => {
    setSessionWarning(false);
    if (sessionWarningTimeout.current) {
      clearTimeout(sessionWarningTimeout.current);
      sessionWarningTimeout.current = null;
    }
    // Reset activity timer
    updateActivity();
  };

  // Refresh user profile data.
  // Checks localStorage directly, not the `token` state variable — this is
  // called from login()/activate()/loginWithGoogle() immediately after
  // setToken(newToken), and `token` here is still the value from before
  // that call (setState doesn't update the current closure's variable, only
  // schedules a future render with the new value). That made every call
  // right after a fresh login silently do nothing (no error, just skipped),
  // so userRoles/subscription never populated until something else (e.g. a
  // full page reload, which re-reads localStorage from scratch) refreshed
  // them — this is why "Admin Tools" only appeared after a manual refresh.
  const refreshUserProfile = async () => {
    if (localStorage.getItem('token')) {
      try {
        const response = await axios.get('/auth/profile');
        setUser(response.data);
        
        // Set roles from profile response (now included in profile)
        const roles = response.data.roles || [];
        setUserRoles(roles);
        console.log('👤 User roles loaded:', roles);
        
        // Fetch subscription info
        try {
          const subResponse = await axios.get('/subscriptions/me');
          setSubscription(subResponse.data);
        } catch (subError) {
          console.log('Could not fetch subscription:', subError.message);
          setSubscription(null);
        }
        
        return response.data;
      } catch (error) {
        console.error('Failed to refresh user profile:', error);
        return null;
      }
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/auth/profile', profileData);
      setUser(response.data); // Update the global user state
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Failed to update profile:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Update failed'
      };
    }
  };

  // Configure axios to include token in requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Restore session from a stored token on app start
  useEffect(() => {
    const checkStoredAuth = async () => {
      const storedToken = localStorage.getItem('token');

      if (storedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          const response = await axios.get('/auth/profile');
          setUser(response.data);
          setToken(storedToken);

          const roles = response.data.roles || [];
          setUserRoles(roles);

          try {
            const subResponse = await axios.get('/subscriptions/me');
            setSubscription(subResponse.data);
          } catch (subError) {
            setSubscription(null);
          }
        } catch (error) {
          console.error('Stored token invalid:', error);
          logout();
        }
      }

      setLoading(false);
    };

    checkStoredAuth();
  }, []);

  // Start/stop session and activity monitoring based on auth state
  useEffect(() => {
    if (user && token) {
      startSessionMonitoring();
      startActivityMonitoring();
    } else {
      stopSessionMonitoring();
      stopActivityMonitoring();
    }

    return () => {
      stopSessionMonitoring();
      stopActivityMonitoring();
    };
  }, [user, token]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSessionMonitoring();
      stopActivityMonitoring();
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      const { user, token: newToken } = response.data;

      setUser(user);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      
      // Fetch roles and subscription after login
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      await refreshUserProfile();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  // Redeems an admin-approval activation code (emailed after a beta signup
  // is approved, see backend/routes/admin.js's approve-beta) and sets the
  // account's password for the first time — replaces the old register()
  // that used to create a brand-new account directly. See
  // backend/routes/auth.js's POST /auth/activate.
  const activate = async (email, code, password) => {
    try {
      const response = await axios.post('/auth/activate', { email, code, password });
      const { user, token: newToken } = response.data;

      setUser(user);
      setToken(newToken);
      localStorage.setItem('token', newToken);

      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      await refreshUserProfile();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Activation failed'
      };
    }
  };

  // Google Sign-In — `credential` is the ID token from Google Identity
  // Services (see components/GoogleSignInButton.js). Backend verifies it
  // and returns a normal app JWT, same shape as login()/activate().
  const loginWithGoogle = async (credential) => {
    try {
      const response = await axios.post('/auth/google', { credential });
      const { user, token: newToken } = response.data;

      setUser(user);
      setToken(newToken);
      localStorage.setItem('token', newToken);

      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      await refreshUserProfile();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Google sign-in failed'
      };
    }
  };

  const logout = async () => {
    setUser(null);
    setUserRoles([]);
    setSubscription(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    userRoles,
    subscription,
    isAdmin,
    token,
    loading,
    sessionWarning,
    // Auth methods
    login,
    activate,
    loginWithGoogle,
    // Logout
    logout,
    // Session management
    extendSession,
    // Other methods
    updateProfile,
    refreshUserProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
